import { Injectable, inject, PLATFORM_ID } from '@angular/core';
import { isPlatformBrowser } from '@angular/common';
import { LoggerService } from './logger.service';

/**
 * GAP-FE-M003 · Centralised, SSR-safe, quota-resilient `localStorage` wrapper.
 *
 * Why this exists (see `AUDIT_FE_ANTIPATTERNS.md` GAP-FE-C013): direct
 * `localStorage.*` access outside services is SSR-unsafe (throws when
 * `localStorage` is undefined during server render), untestable (global, hard
 * to mock), and has no quota/availability handling (Safari private mode and
 * quota-exceeded both throw). This service is a **drop-in** replacement —
 * `getItem`/`setItem`/`removeItem` keep the exact `Storage` signatures, so a
 * migration is `localStorage.X(...)` → `appStorage.X(...)` with identical
 * browser behaviour, plus:
 *   - `isPlatformBrowser` guard (no-op / null on the server),
 *   - try/catch that swallows quota/SecurityError (mirrors callers that already
 *     tolerate failure) and logs at debug,
 *   - typed `getJSON`/`setJSON` convenience for the common parse/stringify pair.
 *
 * NOTE: intentionally does NOT expose `clear()` — clearing all keys is a
 * cross-cutting concern that must not be triggered from feature code; the few
 * legitimate full-clear sites (auth sign-out, test setup) call the platform API
 * directly and are out of this gap's scope.
 */
@Injectable({ providedIn: 'root' })
export class AppStorageService {
  private readonly platformId = inject(PLATFORM_ID);
  private readonly logger = inject(LoggerService);
  private readonly isBrowser = isPlatformBrowser(this.platformId);

  /** Read a raw string value. Returns `null` on the server, on miss, or on error. */
  getItem(key: string): string | null {
    if (!this.isBrowser) {
      return null;
    }
    try {
      return localStorage.getItem(key);
    } catch (error) {
      this.logger.debug('AppStorage.getItem failed', { key, error });
      return null;
    }
  }

  /** Write a raw string value. No-op on the server or on quota/security errors. */
  setItem(key: string, value: string): void {
    if (!this.isBrowser) {
      return;
    }
    try {
      localStorage.setItem(key, value);
    } catch (error) {
      this.logger.debug('AppStorage.setItem failed (quota/availability)', { key, error });
    }
  }

  /** Remove a key. No-op on the server or on error. */
  removeItem(key: string): void {
    if (!this.isBrowser) {
      return;
    }
    try {
      localStorage.removeItem(key);
    } catch (error) {
      this.logger.debug('AppStorage.removeItem failed', { key, error });
    }
  }

  /**
   * Read a raw string value from `sessionStorage` (session-scoped).
   * Returns `null` on the server, on miss, or on error.
   *
   * GAP-FE-C013: session-scoped sibling of `getItem`. Backed by
   * `sessionStorage` (NOT `localStorage`) so callers that depend on
   * session scope (OAuth CSRF state, post-login return URLs, audit
   * session id) keep their exact semantics while gaining the SSR guard,
   * error handling, and mockability of this service.
   */
  getSessionItem(key: string): string | null {
    if (!this.isBrowser) {
      return null;
    }
    try {
      return sessionStorage.getItem(key);
    } catch (error) {
      this.logger.debug('AppStorage.getSessionItem failed', { key, error });
      return null;
    }
  }

  /**
   * Write a raw string value to `sessionStorage` (session-scoped).
   * No-op on the server or on quota/security errors.
   */
  setSessionItem(key: string, value: string): void {
    if (!this.isBrowser) {
      return;
    }
    try {
      sessionStorage.setItem(key, value);
    } catch (error) {
      this.logger.debug('AppStorage.setSessionItem failed (quota/availability)', { key, error });
    }
  }

  /**
   * Remove a key from `sessionStorage` (session-scoped).
   * No-op on the server or on error.
   */
  removeSessionItem(key: string): void {
    if (!this.isBrowser) {
      return;
    }
    try {
      sessionStorage.removeItem(key);
    } catch (error) {
      this.logger.debug('AppStorage.removeSessionItem failed', { key, error });
    }
  }

  /** Parse a JSON value. Returns `null` on the server, miss, or parse/read error. */
  getJSON<T>(key: string): T | null {
    const raw = this.getItem(key);
    if (raw === null) {
      return null;
    }
    try {
      return JSON.parse(raw) as T;
    } catch (error) {
      this.logger.debug('AppStorage.getJSON parse failed', { key, error });
      return null;
    }
  }

  /** Serialise + write a JSON value. No-op on the server or on stringify/quota error. */
  setJSON(key: string, value: unknown): void {
    let serialised: string;
    try {
      serialised = JSON.stringify(value);
    } catch (error) {
      this.logger.debug('AppStorage.setJSON stringify failed', { key, error });
      return;
    }
    this.setItem(key, serialised);
  }
}
